import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Layout, Palette, Code, GripVertical, ChevronUp, ChevronDown, Plus
} from 'lucide-react';

const TABS = [
  { id: 'sections', label: 'Sections', icon: Layout },
  { id: 'styles', label: 'Styles', icon: Palette },
  { id: 'code', label: 'CSS', icon: Code },
];

const GLOBAL_STYLE_FIELDS = [
  { key: 'primaryColor', label: 'Primary Color' },
  { key: 'secondaryColor', label: 'Secondary Color' },
  { key: 'backgroundColor', label: 'Background' },
  { key: 'textColor', label: 'Text Color' },
];

export const EditorSidebar = ({
  activeTab,
  setActiveTab,
  sections = [],
  selectedSection,
  setSelectedSection,
  moveSection,
  onAddSection,
  globalStyles,
  updateGlobalStyles,
  customCSS,
  setCustomCSS,
}) => (
  <div className="w-72 bg-[#302f2c] border-r border-[#1e3a5f]/50 flex flex-col">
    {/* Tabs */}
    <div className="flex border-b border-[#1e3a5f]/50">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1 transition-colors ${
            activeTab === tab.id
              ? 'text-[#7dd3fc] border-b-2 border-[#7dd3fc]'
              : 'text-[#888680] hover:text-[#7dd3fc]'
          }`}
        >
          <tab.icon size={14} />
          {tab.label}
        </button>
      ))}
    </div>

    <div className="flex-1 overflow-y-auto p-4">
      {activeTab === 'sections' && (
        <>
          <div className="space-y-2 mb-4">
            {sections.map((section, index) => (
              <div
                key={section.id}
                onClick={() => setSelectedSection(section.id)}
                className={`p-3 rounded-lg cursor-pointer transition-all group ${
                  selectedSection === section.id
                    ? 'bg-[#7dd3fc] text-[#1a1c1b]'
                    : 'bg-[#1a1c1b] text-[#7dd3fc] hover:bg-[#1e3a5f]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical size={14} className="opacity-50" />
                    <span className="font-medium text-sm truncate">{section.title || section.type}</span>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); moveSection(index, -1); }}
                      className="p-1 hover:bg-black/20 rounded"
                      disabled={index === 0}
                    >
                      <ChevronUp size={12} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveSection(index, 1); }}
                      className="p-1 hover:bg-black/20 rounded"
                      disabled={index === sections.length - 1}
                    >
                      <ChevronDown size={12} />
                    </button>
                  </div>
                </div>
                <span className="text-xs opacity-70 capitalize">{section.type}</span>
              </div>
            ))}
          </div>

          <Button
            onClick={onAddSection}
            variant="outline"
            data-testid="add-section-btn"
            className="w-full border-dashed border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-lg py-3"
          >
            <Plus size={16} className="mr-2" />
            Add Section
          </Button>
        </>
      )}

      {activeTab === 'styles' && (
        <div className="space-y-4">
          <h3 className="text-[#7dd3fc] font-semibold text-sm uppercase tracking-wider">Global Styles</h3>
          {GLOBAL_STYLE_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label className="text-[#888680] text-xs mb-1 block">{label}</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={globalStyles[key] || '#ffffff'}
                  onChange={(e) => updateGlobalStyles(key, e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer bg-transparent border border-[#1e3a5f]"
                />
                <Input
                  value={globalStyles[key] || ''}
                  onChange={(e) => updateGlobalStyles(key, e.target.value)}
                  placeholder="#000000"
                  className="flex-1 bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] text-sm h-10"
                />
              </div>
            </div>
          ))}

          <div>
            <label className="text-[#888680] text-xs mb-1 block">Font Family</label>
            <Input
              value={globalStyles.fontFamily || 'Inter'}
              onChange={(e) => updateGlobalStyles('fontFamily', e.target.value)}
              className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] text-sm h-10"
            />
          </div>
        </div>
      )}

      {activeTab === 'code' && (
        <div>
          <h3 className="text-[#7dd3fc] font-semibold text-sm uppercase tracking-wider mb-2">Custom CSS</h3>
          <Textarea
            value={customCSS}
            onChange={(e) => setCustomCSS(e.target.value)}
            placeholder="/* Add custom CSS here */"
            rows={20}
            className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] font-mono text-xs resize-none"
          />
        </div>
      )}
    </div>
  </div>
);
